import { CancelCircleIcon, CheckmarkCircle02Icon, Clock01Icon, MinusSignCircleIcon } from '@hugeicons/core-free-icons';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { Badge, type BadgeTone } from '../primitives/badge.js';
import { Icon } from '../primitives/icon.js';
import { Spinner } from '../primitives/spinner.js';

export type ActivityStepStatus = 'running' | 'done' | 'failed' | 'skipped';

export interface ActivityStepProps {
  label: string;
  status: ActivityStepStatus;
  detail?: string | null;
  /** Who/where (contact, project), shown muted under the label. */
  meta?: React.ReactNode;
  /** Formatted time of the step. */
  time?: string;
  /** Nested steps (e.g. the steps of one AI turn). */
  children?: React.ReactNode;
  className?: string;
}

const STATUS_TEXT: Record<ActivityStepStatus, string> = { running: 'En curso', done: 'Hecho', failed: 'Falló', skipped: 'Omitido' };

function StatusMark({ status }: { status: ActivityStepStatus }) {
  if (status === 'running') return <Spinner size="sm" className="text-accent" />;
  const icon = status === 'done' ? CheckmarkCircle02Icon : status === 'failed' ? CancelCircleIcon : MinusSignCircleIcon;
  return <Icon icon={icon} aria-hidden className={cn('size-4', status === 'done' ? 'text-success' : status === 'failed' ? 'text-danger' : 'text-text-secondary')} />;
}

/**
 * One unit of LIA's work in the live activity feed. A running step shows a spinner and a softly pulsing label — the
 * «thinking» state — and settles into done / failed / skipped. Status is always icon + text (screen readers).
 */
export function ActivityStep({ label, status, detail, meta, time, children, className }: ActivityStepProps) {
  return (
    <li className={cn('grid gap-1', className)}>
      <div className="flex items-start gap-2">
        <span className="mt-0.5 flex size-4 shrink-0 items-center justify-center">
          <StatusMark status={status} />
        </span>
        <div className="grid min-w-0 flex-1 gap-0.5">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span className={cn('text-body', status === 'running' ? 'font-medium text-text-primary motion-safe:animate-pulse' : status === 'skipped' ? 'text-text-secondary' : 'text-text-primary')}>
              {label}
              {status === 'running' ? '…' : ''}
            </span>
            <span className="sr-only">{STATUS_TEXT[status]}</span>
            {time ? <span className="text-meta text-text-secondary">{time}</span> : null}
          </div>
          {meta ? <div className="text-meta text-text-secondary">{meta}</div> : null}
          {detail ? <p className={cn('text-meta', status === 'failed' ? 'text-danger' : 'text-text-secondary')}>{detail}</p> : null}
        </div>
      </div>
      {children ? <ul className="ml-2 grid gap-2 border-l border-border-subtle pl-4">{children}</ul> : null}
    </li>
  );
}

/** List wrapper: new steps are announced politely to screen readers. */
export function ActivityTimeline({ children, label = 'Actividad de LIA', className }: { children: React.ReactNode; label?: string; className?: string }) {
  return (
    <ul aria-label={label} aria-live="polite" className={cn('grid gap-3', className)}>
      {children}
    </ul>
  );
}

export interface AgentTaskCardProps {
  title: string;
  /** Formatted due time. */
  due: string;
  owner: string | null;
  context?: string | null;
  /** Contact · project. */
  meta?: React.ReactNode;
  state: 'open' | 'overdue' | 'missing' | 'done' | 'cancelled';
  /** Result label of a closed task. */
  result?: string | null;
  resultNote?: string | null;
  /** «En el CRM» link or note. */
  footer?: React.ReactNode;
  className?: string;
}

const TASK_STATE: Record<AgentTaskCardProps['state'], { tone: BadgeTone; text: string }> = {
  open: { tone: 'info', text: 'Pendiente' },
  overdue: { tone: 'danger', text: 'Vencida' },
  missing: { tone: 'warning', text: 'Sin siguiente acción' },
  done: { tone: 'success', text: 'Cerrada' },
  cancelled: { tone: 'neutral', text: 'Sustituida' },
};

/** LIA's task on an opportunity: action + objective, due time, owner, context; closed tasks show their result. */
export function AgentTaskCard({ title, due, owner, context, meta, state, result, resultNote, footer, className }: AgentTaskCardProps) {
  const s = TASK_STATE[state];
  return (
    <article className={cn('grid gap-1.5 rounded-card border border-border-subtle bg-surface-default p-3', state === 'overdue' && 'border-danger', className)}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-body font-medium text-text-primary">{title}</h3>
        <Badge tone={s.tone}>{s.text}</Badge>
      </div>
      {meta ? <div className="text-meta text-text-secondary">{meta}</div> : null}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-meta text-text-secondary">
        <span className="inline-flex items-center gap-1">
          <Icon icon={Clock01Icon} aria-hidden className="size-3.5" />
          {due}
        </span>
        {owner ? <span>Responsable: {owner}</span> : null}
      </div>
      {context ? <p className="text-meta text-text-secondary">{context}</p> : null}
      {result ? (
        <p className="text-meta text-text-primary">
          Resultado: {result}
          {resultNote ? <span className="text-text-secondary"> — {resultNote}</span> : null}
        </p>
      ) : null}
      {footer ? <div className="text-meta">{footer}</div> : null}
    </article>
  );
}
